import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import SubscribeFilterComponent from "./Subscribtionfilter";
import "../css/SubscriptionDashboard.css";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import api from "../api";
import subscriptionDog from "../assets/Subscription-dog.png";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const planVariants = {
  initial: { opacity: 0, y: 20, scale: 0.95 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -20, scale: 0.95 },
};

export default function SubscriptionDashboard({
  ribbonMap,
  onDeleteRequest,
  plans = [],
  durationMap, // ✅ added from parent
}) {
  const [showAll, setShowAll] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState("All");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [subscribers, setSubscribers] = useState([]);
  const [filteredTotal, setFilteredTotal] = useState(null);
  const [overallTotal, setOverallTotal] = useState(0);

  const navigate = useNavigate();
  const prevPlanCount = useRef(plans.length);

  // ✅ Show success toast when a plan is deleted (parent updates props)
  useEffect(() => {
    if (plans.length < prevPlanCount.current) {
      toast.success("Subscription plan deleted successfully!", {
        position: "top-right",
        autoClose: 3000,
        theme: "colored",
      });
    }
    prevPlanCount.current = plans.length;
  }, [plans]);

  // ✅ Helper: format date for backend
  const formatDateForBackend = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${day}-${month}-${year}`;
  };

  // ✅ Fetch subscriber & totals data
  const fetchData = async (duration = undefined, from = "", to = "") => {
    try {
      const formattedFrom = formatDateForBackend(from);
      const formattedTo = formatDateForBackend(to);

      const res = await api.get("/subscription-plans/", {
        params: {
          subscription_duration: duration
            ? duration.toLowerCase().replace(" ", "_")
            : undefined,
          from_date: formattedFrom || undefined,
          to_date: formattedTo || undefined,
        },
      });

      const data = res.data;
      setOverallTotal(data.total_subscriptions_amount_all_plans || 0);

      if (data.total_subscriptions_amount_filtered !== null) {
        setFilteredTotal(data.total_subscriptions_amount_filtered);
      } else {
        setFilteredTotal(data.total_subscriptions_amount_all_plans || 0);
      }

      const allSubs = data.subscription_plans.flatMap(
        (plan) => plan.my_subscribers || []
      );
      setSubscribers(allSubs);
    } catch (err) {
      console.error("Failed to fetch subscription data:", err);
    }
  };

  useEffect(() => {
    const durationParam =
      selectedDuration === "All"
        ? undefined
        : selectedDuration.toLowerCase().replace(" ", "_");
    fetchData(durationParam, fromDate, toDate);
  }, [selectedDuration, fromDate, toDate]);

  const toggleView = () => setShowAll((prev) => !prev);
  const visibleSubscribers = showAll ? subscribers : subscribers.slice(0, 5);

  const handleEdit = (plan) => {
    navigate("/add-subscription", { state: { plan } });
  };

  const handleApplyFilter = (from, to) => {
    setFromDate(from);
    setToDate(to);

    const durationParam =
      selectedDuration === "All"
        ? undefined
        : selectedDuration.toLowerCase().replace(" ", "_");

    fetchData(durationParam, from, to);
  };

  return (
    <div className="subscription-dashboard-container">
      {/* Left Section */}
      <div className="subscription-dashboard-left">
        <div className="subscription-dashboard-card">
          <h3 className="subscription-dashboard-title">My Subscribers</h3>
          <div className="subscription-dashboard-header">
            <span>Due For Next 1 Week</span>
            <button
              className="subscription-dashboard-view-all"
              onClick={toggleView}
            >
              {showAll ? "Close" : "View All"}
            </button>
          </div>

          <motion.div
            className="subscription-dashboard-body"
            initial={false}
            animate={{ height: showAll ? "auto" : 380 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
          >
            {visibleSubscribers.length > 0 ? (
              visibleSubscribers.map((user) => (
                <div
                  key={user.id}
                  className="subscription-dashboard-subscriber-row"
                >
                  <img
                    src={
                      user.profile_image
                        ? `${api.defaults.baseURL.split("/api")[0]}${
                            user.profile_image
                          }`
                        : "/default-avatar.png"
                    }
                    alt={`${user.fullName}'s avatar`}
                  />
                  <span className="subscription-dashboard-subscriber-name">
                    {user.fullName}
                  </span>
                  <span className="subscription-dashboard-sub-type">
                    {durationMap?.[user.subscription_duration] ||
                      user.subscription_duration ||
                      "N/A"}
                  </span>
                  <span className="subscription-dashboard-due-status">
                    Subscribed on {user.subscribed_on}
                  </span>
                  <button
                    className="subscription-dashboard-notify-btn"
                    onClick={() =>
                      toast.success(`Notification sent to ${user.fullName}`)
                    }
                  >
                    Send Notification
                  </button>
                </div>
              ))
            ) : (
              <p>No subscribers found.</p>
            )}
          </motion.div>
        </div>

        {/* Total Subscription Summary */}
        <div className="subscription-dashboard-summary">
          <div className="subscription-dashboard-total-subscriptions-header">
            <h3>Total Subscriptions</h3>
            <div className="subscription-dashboard-tabs">
              {["All", "Monthly", "Quarterly", "Half Yearly", "Yearly"].map(
                (tab) => (
                  <button
                    key={tab}
                    className={`subscription-dashboard-tab ${
                      selectedDuration === tab ? "active" : ""
                    }`}
                    onClick={() => setSelectedDuration(tab)}
                  >
                    {tab}
                  </button>
                )
              )}
              <SubscribeFilterComponent
                fromDate={fromDate}
                toDate={toDate}
                setFromDate={setFromDate}
                setToDate={setToDate}
                handleApplyFilter={handleApplyFilter}
              />
            </div>
          </div>

          <div className="subscription-dashboard-total-body">
            <img
              src={subscriptionDog}
              alt="Dog with money"
              className="subscription-dashboard-dog-img"
            />
            <div className="subscription-dashboard-total-text">
              <h2 className="subscription-dashboard-total-title">
                Pawscription Profits
              </h2>
              <p className="subscription-dashboard-total-amount">
                ₹
                {filteredTotal !== null
                  ? filteredTotal.toLocaleString()
                  : overallTotal.toLocaleString()}{" "}
                <span>/ selected range</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Section */}
      <div className="subscription-dashboard-right">
        <div className="subscription-dashboard-plans-section">
          <h3 className="subscription-dashboard-plans-title">
            Subscription Plans
          </h3>

          <AnimatePresence>
            {plans.map((plan) => {
              const planKey = plan.subscription_duration
                ?.toLowerCase()
                .replace(" ", "-");
              const ribbon = ribbonMap?.[planKey];
              const readableDuration =
                durationMap?.[plan.subscription_duration] ||
                plan.subscription_duration;

              return (
                <motion.div
                  key={plan.id}
                  className={`subscription-dashboard-plan-card subscription-dashboard-${planKey}`}
                  variants={planVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  layout
                >
                  {ribbon && (
                    <div className="subscription-dashboard-ribbon">
                      <img
                        src={ribbon}
                        alt={`${readableDuration} Ribbon`}
                      />
                      <span className="subscription-dashboard-ribbon-text">
                        {readableDuration}
                      </span>
                    </div>
                  )}

                  <p className="subscription-dashboard-amount">
                    <strong>Amount:</strong> ₹{plan.subscription_amount} /{" "}
                    <span>{readableDuration}</span>
                  </p>

                  <ul>
                    {plan.services
                      ?.split(",")
                      .map((feature, idx) => (
                        <li key={idx}>{feature.trim()}</li>
                      ))}
                  </ul>

                  <div className="button-container">
                    <button
                      className="subscription-dashboard-edit-btn"
                      title="Edit Plan"
                      onClick={() => handleEdit(plan)}
                    >
                      Edit
                    </button>

                    {/* ✅ Connected Delete Button to Parent Modal */}
                    <button
                      className="subscription-dashboard-delete-btn"
                      title="Delete Plan"
                      onClick={() =>
                        onDeleteRequest(
                          plan.id,
                          "plan",
                          plan.plan_name || readableDuration
                        )
                      }
                    >
                      Delete
                    </button>

                    <button
                      className="subscription-dashboard-view-subscribers-btn"
                      title="View Subscribers"
                      onClick={() =>
                        navigate("/view-subscribers", { state: { plan } })
                      }
                    >
                      <MdOutlineRemoveRedEye /> View Subscribers
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
}
